import { createAsyncThunk, miniSerializeError } from '@reduxjs/toolkit';

import can_user_save_items from 'functions/server/can_user_save_items';
import saved_items_by_user from 'functions/server/saved_items_by_user';
import saved_items_create from 'functions/server/saved_items_create';
import saved_items_delete from 'functions/server/saved_items_delete';
import saved_items_update from 'functions/server/saved_items_update';
import { normalizeTag, normalizeTags } from 'store/functions/libraryTags';

/*
The library's server calls.

Mirrors planThunks: thin wrappers whose only job is to normalise the response
into the shape the slice stores. Deliberately NOT part of inputData — the
library belongs to the account, not to the scenario, so it must not enter the
undo history or ride along in a /save_instance body.

Mutations patch the slice from what the server returns rather than refetching:
every endpoint answers with the full record, so a refetch would be a second
round trip to learn what we were just told.
*/

const itemsOf = (response) => {
  const items = response?.items;
  return Array.isArray(items) ? items : [];
};

export const fetchLibraryItems = createAsyncThunk(
  'library/fetchItems',
  async () => itemsOf(await saved_items_by_user()),
  {
    condition: (arg, { getState }) => {
      // Opening a picker should not refetch a library the account page just
      // loaded. `force` is for after a save from another tab, or a retry.
      if (arg?.force) return true;
      const status = getState()?.library?.status;
      return status !== 'loading' && status !== 'loaded';
    },
  },
);

/**
 * A rejected request, keeping what the save dialog needs to explain it.
 *
 * Redux Toolkit's default serializer keeps only name, message, stack and code, so
 * `.unwrap()` handed SaveToLibraryDialog an error with no `details` and no
 * `status`. describeSaveError keys everything on `details.reason` and
 * `details.index`, so in the running app none of its row-naming reasons could
 * ever fire (its unit tests pass it a raw Error, which is why nobody saw), and a
 * dropped connection could not be told apart from a server answer. These fields
 * are plain JSON from the backend, and saveLibraryItems.rejected is not stored.
 *
 * @param {unknown} error
 * @returns {object}
 */
export const serializeRequestError = (error) => {
  const serialized = miniSerializeError(error);
  if (error && typeof error === 'object') {
    if (typeof error.status === 'number') serialized.status = error.status;
    if (error.details && typeof error.details === 'object') serialized.details = error.details;
    if (typeof error.backendMessage === 'string') serialized.backendMessage = error.backendMessage;
  }
  return serialized;
};

export const saveLibraryItems = createAsyncThunk(
  'library/saveItems',
  async (items) => itemsOf(await saved_items_create(items)),
  { serializeError: serializeRequestError },
);

export const updateLibraryItem = createAsyncThunk(
  'library/updateItem',
  async ({ itemId, changes }) => {
    const response = await saved_items_update(itemId, changes);
    return response?.item || null;
  },
);

/**
 * Add or remove one tag across many items at once.
 *
 * Tags exist to name a GROUP — "all my Indianapolis depots", "the firetrucks" —
 * so the operation that matters is applying one to a selection. Doing that a
 * row at a time is exactly the tedium the library was built to remove.
 *
 * Adding is a UNION, never a replace: each selected item keeps the tags it
 * already carries. Two items in one selection rarely have identical tags, and
 * overwriting would quietly destroy the ones an earlier grouping depends on.
 *
 * The endpoint updates a single item, so this fans out. Failures are counted
 * per item rather than aborting the batch: a blip on item 14 of 30 should not
 * leave the user unsure which ones took.
 */
export const tagLibraryItems = createAsyncThunk(
  'library/tagItems',
  async ({ itemIds, tag, mode = 'add' }, { getState }) => {
    const normalized = normalizeTag(tag);
    if (!normalized) return { updated: [], failed: 0 };

    const byId = new Map(
      (getState().library?.items || []).map((item) => [item.item_id, item]));

    const results = await Promise.allSettled(itemIds.map((itemId) => {
      const current = byId.get(itemId)?.tags || [];
      const tags = mode === 'remove'
        ? current.filter((existing) => existing !== normalized)
        : normalizeTags([...current, normalized]);
      // Already says what we were going to say: skip the call rather than
      // spend a request writing a value back onto itself.
      const unchanged = tags.length === current.length
        && tags.every((value, index) => value === current[index]);
      if (unchanged) return Promise.resolve(null);
      return saved_items_update(itemId, { tags }).then((r) => r?.item || null);
    }));

    return {
      updated: results
        .filter((r) => r.status === 'fulfilled' && r.value)
        .map((r) => r.value),
      failed: results.filter((r) => r.status === 'rejected').length,
    };
  },
);

export const deleteLibraryItems = createAsyncThunk(
  'library/deleteItems',
  async (itemIds) => {
    await saved_items_delete(itemIds);
    // The server reports how many rows it removed, but unknown ids are silently
    // not counted, so that number cannot tell us WHICH went. The ids we asked
    // to remove are the ones to drop locally either way.
    return itemIds;
  },
);

/** Pre-flight, so a full form is never met with a 403. Not a slice action —
 *  the answer is about a hypothetical save, not about current state. */
export const checkCanSaveItems = async (itemType, count) => {
  try {
    const response = await can_user_save_items(itemType, count);
    return {
      allowed: response?.allowed !== false,
      remaining: response?.remaining ?? null,
      limit: response?.limit ?? null,
      tier: response?.tier || null,
    };
  } catch {
    // Fail open, matching how entitlements behave everywhere else: a broken
    // pre-flight must not stop someone saving. The server enforces the real cap,
    // and a rejected save is explained in the dialog (describeSaveError).
    //
    // That includes a 404 USER_NOT_FOUND, the backend's answer for a free account
    // with no paid subscription on /user_plan and /can_user_save. This route
    // resolves the plan through the entitlement layer, which already maps that to
    // the free tier, so it should not arrive here; if it ever does, the save still
    // goes ahead and /saved_items_create enforces the free quota. Nothing is
    // hidden from the person by failing open: no quota is shown, and the save
    // itself either succeeds or says why it did not.
    return { allowed: true, remaining: null, limit: null, tier: null };
  }
};
